import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeOff, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IDLE_TIMEOUT = 9 * 60 * 1000; // 9 minutes in milliseconds

export default function IdleScreen() {
  const [isIdle, setIsIdle] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, logout } = useAuth();

  useEffect(() => {
    let idleTimer;
    
    const startIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        setIsIdle(true);
      }, IDLE_TIMEOUT);
    };

    // Events that reset the idle timer
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      if (isIdle) {
        // Don't auto-reset when idle - user must unlock with password
        return;
      }
      startIdleTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the timer initially
    startIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isIdle]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Digite sua senha');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/security/unlock-screen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsIdle(false);
        setPassword('');
        setError('');
        toast.success('Bem-vindo de volta!');
      } else {
        setError(data.detail || 'Senha incorreta');
        setPassword('');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (err) {
      window.location.href = '/login';
    }
  };

  if (!isIdle) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/4058026/pexels-photo-4058026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay escuro para melhor legibilidade */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Conteúdo central */}
      <div className="relative z-10 text-center px-6 max-w-2xl w-full">
        {/* Logo - Chave dourada */}
        <div className="flex justify-center mb-6">
          <svg 
            viewBox="0 0 24 24" 
            className="w-16 h-16 text-amber-500"
            fill="currentColor"
          >
            <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
          </svg>
        </div>
        
        {/* Nome da marca */}
        <h1 className="text-5xl md:text-6xl font-serif text-white mb-4 tracking-wide">
          Author's Key
        </h1>
        
        {/* Slogan principal */}
        <p className="text-xl md:text-2xl text-stone-200 mb-2 font-light">
          Onde ideias viram livros.
        </p>
        <p className="text-xl md:text-2xl text-stone-200 mb-8 font-light">
          Seu ateliê literário completo.
        </p>
        
        {/* Card de desbloqueio */}
        <div className="bg-stone-900/80 backdrop-blur-sm rounded-2xl p-8 border border-stone-700/50 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-amber-500" />
            <span className="text-stone-300 text-sm uppercase tracking-wider">
              Sessão Pausada
            </span>
          </div>
          
          {/* Email do usuário */}
          {user?.email && (
            <p className="text-stone-400 text-sm mb-4">
              {user.email}
            </p>
          )}
          
          {/* Convite para voltar */}
          <p className="text-amber-400/90 font-serif text-xl mb-6 italic">
            "Sua história aguarda..."
          </p>
          
          {/* Formulário de senha */}
          <form onSubmit={handleUnlock} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Digite sua senha"
                className="w-full bg-stone-800/80 border border-stone-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                disabled={isLoading}
                autoFocus
                data-testid="idle-screen-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-200 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Mensagem de erro */}
            {error && (
              <p className="text-red-400 text-sm" data-testid="idle-screen-error">
                {error}
              </p>
            )}
            
            {/* Botão de desbloquear */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-stone-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              data-testid="idle-screen-unlock-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Voltar a Escrever'
              )}
            </button>
          </form>
          
          {/* Link para sair */}
          <button
            onClick={handleLogout}
            className="mt-4 text-stone-500 hover:text-stone-300 text-sm flex items-center justify-center gap-1 mx-auto transition-colors"
            data-testid="idle-screen-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Não é você? Sair
          </button>
        </div>
        
        {/* Descrição secundária */}
        <p className="text-stone-500 text-sm mt-8">
          Organize, escreva, acompanhe e desenvolva suas obras<br />
          com elegância e simplicidade.
        </p>
      </div>
    </div>
  );
}
